import { useState, useEffect, useCallback } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { nextBusinessDay, ymdLocal, formatBR } from "@/lib/holidays";
import { Textarea } from "@/components/ui/textarea";
import {
  Wallet, TrendingUp, Clock, Gift, Users, AlertTriangle, CheckCircle2, Loader2,
  CalendarClock, FileDown, CalendarDays, Check, RotateCcw, Layers, Phone, FileText, UserX,
  BarChart3, ArrowUpRight, ArrowDownRight, MessageCircle, Settings,
} from "lucide-react";

const METHODS = ["Pix", "Dinheiro", "Crédito à Vista", "Crédito Parcelado", "Bonificado"];
const formatBRL = (v) => {
  const n = Number(v) || 0;
  try { return n.toLocaleString("pt-BR", { style: "currency", currency: "BRL" }); }
  catch (e) { return "R$ " + n.toFixed(2).replace(".", ","); }
};
const formatDateBR = (iso) => {
  if (!iso) return "--/--/----";
  const [y, m, d] = iso.split("-");
  return `${d}/${m}/${y}`;
};
const monthLabel = (ym) => {
  if (!ym) return "";
  const [y, m] = ym.split("-").map(Number);
  const nomes = ["Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho", "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro"];
  return `${nomes[m - 1]} de ${y}`;
};

const inputCls = "h-11 bg-[#181F2C] border border-[#263147] text-slate-100 rounded-lg px-3 text-sm";
const selectCls = "h-11 bg-[#181F2C] border-[#263147] text-slate-100 rounded-lg";

/* ------------------------- Resumo do Mês ------------------------- */
const StatCard = ({ icon: Icon, label, value, accent, testid, sub }) => (
  <div data-testid={testid} className="bg-[#0F1520] border border-[#263147] rounded-2xl p-5">
    <div className="flex items-center gap-2 mb-2">
      <div className={`w-9 h-9 rounded-xl flex items-center justify-center ${accent}`}><Icon className="w-4 h-4" /></div>
      <span className="text-xs text-slate-400 font-medium">{label}</span>
    </div>
    <p className="text-2xl font-bold font-heading text-slate-100 tabular-nums">{value}</p>
    {sub && <p className="text-xs text-slate-500 mt-1">{sub}</p>}
  </div>
);

function ResumoMes() {
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/financial/summary`, { params: { month } });
      setData(data);
    } catch { toast.error("Não foi possível carregar o resumo."); }
    finally { setLoading(false); }
  }, [month]);
  useEffect(() => { load(); }, [load]);

  const pct = data && data.total_expected > 0 ? Math.min(100, Math.round((data.total_received / data.total_expected) * 100)) : 0;
  const totalStudents = data ? data.students_up_to_date + data.students_overdue : 0;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-end gap-2">
        <label className="text-xs text-slate-400">Mês</label>
        <input type="month" data-testid="financeiro-month-input" value={month} onChange={(e) => setMonth(e.target.value)} className={inputCls} />
      </div>
      {loading ? (
        <div className="flex items-center justify-center py-20 text-slate-400"><Loader2 className="w-6 h-6 animate-spin mr-2" /> Carregando...</div>
      ) : !data ? null : (
        <>
          <p className="text-sm text-slate-400">Resumo de {monthLabel(month)}</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <StatCard testid="stat-expected" icon={Wallet} label="Esperado no mês" value={formatBRL(data.total_expected)} accent="bg-slate-500/15 text-slate-300" />
            <StatCard testid="stat-received" icon={TrendingUp} label="Recebido até hoje" value={formatBRL(data.total_received)} accent="bg-emerald-500/15 text-emerald-400" sub={`${data.counts.pago} pagamento(s)`} />
            <StatCard testid="stat-pending" icon={Clock} label="Pendente a receber" value={formatBRL(data.total_pending)} accent="bg-amber-500/15 text-amber-400" sub={`${data.counts.a_vencer} a vencer • ${data.counts.vencido} vencido(s)`} />
            <StatCard testid="stat-bonificado" icon={Gift} label="Bonificado" value={formatBRL(data.total_bonificado)} accent="bg-blue-500/15 text-blue-400" sub={`${data.counts.bonificado} lançamento(s)`} />
          </div>
          <div className="bg-[#0F1520] border border-[#263147] rounded-2xl p-5">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-slate-300 font-medium">Recebimento do mês</span>
              <span className="text-sm text-emerald-400 font-semibold tabular-nums" data-testid="progress-pct">{pct}%</span>
            </div>
            <div className="h-3 rounded-full bg-[#181F2C] overflow-hidden">
              <div data-testid="progress-bar" className="h-full bg-emerald-500 rounded-full transition-all duration-500" style={{ width: `${pct}%` }} />
            </div>
            <div className="flex items-center justify-between mt-2 text-xs text-slate-500">
              <span>Recebido: {formatBRL(data.total_received)}</span><span>Esperado: {formatBRL(data.total_expected)}</span>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div data-testid="stat-up-to-date" className="bg-[#0F1520] border border-[#263147] rounded-2xl p-5">
              <div className="flex items-center gap-2 mb-1">
                <CheckCircle2 className="w-5 h-5 text-emerald-400" /><span className="text-sm text-slate-300 font-medium">Alunos em dia</span>
                <span className="text-[10px] text-slate-500 ml-auto">(geral)</span>
              </div>
              <p className="text-3xl font-bold font-heading text-emerald-400 tabular-nums">{data.students_up_to_date}<span className="text-base text-slate-500 font-normal"> / {totalStudents}</span></p>
            </div>
            <div data-testid="stat-overdue" className="bg-[#0F1520] border border-[#263147] rounded-2xl p-5">
              <div className="flex items-center gap-2 mb-1">
                <AlertTriangle className="w-5 h-5 text-red-400" /><span className="text-sm text-slate-300 font-medium">Alunos inadimplentes</span>
                <span className="text-[10px] text-slate-500 ml-auto">(geral)</span>
              </div>
              <p className="text-3xl font-bold font-heading text-red-400 tabular-nums">{data.students_overdue}<span className="text-base text-slate-500 font-normal"> / {totalStudents}</span></p>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-3 text-xs">
            <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-full bg-emerald-500" /> Pago</span>
            <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-full bg-amber-500" /> A vencer</span>
            <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-full bg-red-500" /> Vencido</span>
            <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-full bg-blue-500" /> Bonificação</span>
          </div>
          {data.overdue_students?.length > 0 && (
            <div data-testid="overdue-list" className="bg-[#0F1520] border border-red-500/30 rounded-2xl p-5">
              <div className="flex items-center gap-2 mb-3"><Users className="w-4 h-4 text-red-400" /><span className="text-sm text-slate-200 font-medium">Alunos com parcelas vencidas</span></div>
              <div className="space-y-1.5">
                {data.overdue_students.map((s) => (
                  <div key={s.id} data-testid="overdue-student-row" className="flex items-center gap-2 bg-[#181F2C] border border-[#263147] rounded-lg px-3 py-2 text-sm">
                    <span className="w-2 h-2 rounded-full bg-red-500 shrink-0" /><span className="text-slate-200">{s.full_name}</span>
                    {s.nickname && <span className="text-slate-500 text-xs">({s.nickname})</span>}
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

/* ------------------------- Contas a Receber ------------------------- */
function ContasReceber() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reschedule, setReschedule] = useState(null); // {student_id, pid, date}

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/financial/receivables`);
      setItems(data.items || []);
    } catch { toast.error("Não foi possível carregar as contas a receber."); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const registrar = async (it) => {
    try {
      await api.patch(`/financial/payment`, { student_id: it.student_id, pid: it.pid, received: true });
      toast.success("Recebimento registrado!");
      load();
    } catch { toast.error("Falha ao registrar recebimento."); }
  };
  const abrirReagendamento = (it) => {
    const sug = nextBusinessDay(new Date());
    setReschedule({ student_id: it.student_id, pid: it.pid, date: ymdLocal(sug), suggested: formatBR(sug), name: it.student_name });
  };
  const salvarReagendamento = async () => {
    if (!reschedule?.date) { toast.error("Escolha a nova data."); return; }
    try {
      await api.patch(`/financial/payment`, { student_id: reschedule.student_id, pid: reschedule.pid, date: reschedule.date });
      toast.success("Parcela reagendada!");
      setReschedule(null);
      load();
    } catch { toast.error("Falha ao reagendar."); }
  };

  const total = items.reduce((s, i) => s + (i.amount || 0), 0);

  return (
    <div className="space-y-4" data-testid="contas-receber">
      <div className="flex items-center justify-between">
        <p className="text-sm text-slate-400">{items.length} pendência(s) • total {formatBRL(total)}</p>
        <div className="flex items-center gap-2 text-xs">
          <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-full bg-red-500" /> Vencido</span>
          <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-full bg-amber-500" /> A vencer (mês)</span>
        </div>
      </div>
      {loading ? (
        <div className="flex items-center justify-center py-20 text-slate-400"><Loader2 className="w-6 h-6 animate-spin mr-2" /> Carregando...</div>
      ) : items.length === 0 ? (
        <div className="text-center py-16 text-slate-500 text-sm">Nenhuma conta pendente. Tudo em dia! 🎉</div>
      ) : (
        <div className="space-y-2">
          {items.map((it) => {
            const overdue = it.status === "vencido";
            const upcomingMonth = it.status === "a_vencer" && it.in_month;
            const accent = overdue ? "border-red-500/40" : upcomingMonth ? "border-amber-500/40" : "border-[#263147]";
            const dot = overdue ? "bg-red-500" : upcomingMonth ? "bg-amber-500" : "bg-slate-500";
            return (
              <div key={it.pid} data-testid="receivable-row" className={`bg-[#0F1520] border ${accent} rounded-xl p-3 flex flex-col sm:flex-row sm:items-center gap-3`}>
                <span className={`w-2.5 h-2.5 rounded-full ${dot} shrink-0`} />
                <div className="flex-1 min-w-0">
                  <p className="text-slate-100 font-medium truncate">{it.student_name}{it.nickname && <span className="text-slate-500 text-xs font-normal"> ({it.nickname})</span>}</p>
                  <p className="text-xs text-slate-400">{it.label}</p>
                </div>
                <div className="flex items-center gap-4 sm:gap-6">
                  <div className="text-right">
                    <p className="text-xs text-slate-500 flex items-center gap-1"><CalendarDays className="w-3 h-3" /> {formatDateBR(it.date)}</p>
                    <p className="text-emerald-400 font-semibold tabular-nums">{formatBRL(it.amount)}</p>
                  </div>
                  <div className="flex gap-2">
                    <Button type="button" size="sm" data-testid="receivable-register-button" onClick={() => registrar(it)} className="h-9 bg-emerald-500 hover:bg-emerald-600 text-[#0A0D12] font-semibold rounded-lg px-3"><Check className="w-4 h-4 sm:mr-1" /><span className="hidden sm:inline">Receber</span></Button>
                    <Button type="button" size="sm" variant="outline" data-testid="receivable-reschedule-button" onClick={() => abrirReagendamento(it)} className="h-9 border-[#263147] bg-[#181F2C] text-slate-200 hover:bg-[#1E2738] rounded-lg px-3"><CalendarClock className="w-4 h-4 sm:mr-1" /><span className="hidden sm:inline">Reagendar</span></Button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <Dialog open={!!reschedule} onOpenChange={(o) => !o && setReschedule(null)}>
        <DialogContent className="bg-[#0F1520] border-[#263147] text-slate-100">
          <DialogHeader><DialogTitle>Reagendar parcela</DialogTitle><DialogDescription className="text-slate-400">Escolha a nova data de vencimento desta parcela.</DialogDescription></DialogHeader>
          <div className="space-y-2">
            <p className="text-sm text-slate-400">{reschedule?.name}</p>
            {reschedule?.suggested && (
              <div data-testid="reschedule-suggested" className="flex items-center gap-2 bg-emerald-500/10 border border-emerald-500/30 rounded-lg px-3 py-2 text-sm text-emerald-300">
                <CalendarClock className="w-4 h-4 shrink-0" />
                <span>Data sugerida: <span className="font-semibold text-emerald-200">{reschedule.suggested}</span> — próximo dia útil</span>
              </div>
            )}
            <Label className="text-slate-300 text-xs">Nova data de vencimento</Label>
            <Input type="date" data-testid="reschedule-date-input" value={reschedule?.date || ""} onChange={(e) => setReschedule((r) => ({ ...r, date: e.target.value }))} className={selectCls} />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setReschedule(null)} className="border-[#263147] bg-[#181F2C] text-slate-200 hover:bg-[#1E2738]">Cancelar</Button>
            <Button type="button" data-testid="reschedule-save-button" onClick={salvarReagendamento} className="bg-emerald-500 hover:bg-emerald-600 text-[#0A0D12] font-semibold">Salvar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ------------------------- Pagamentos Recebidos ------------------------- */
function PagamentosRecebidos() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [students, setStudents] = useState([]);
  const [filters, setFilters] = useState({ start: "", end: "", student_id: "all", method: "all" });

  useEffect(() => {
    api.get(`/students`).then(({ data }) => setStudents(data || [])).catch(() => {});
  }, []);

  const buildParams = useCallback(() => {
    const p = {};
    if (filters.start) p.start = filters.start;
    if (filters.end) p.end = filters.end;
    if (filters.student_id !== "all") p.student_id = filters.student_id;
    if (filters.method !== "all") p.method = filters.method;
    return p;
  }, [filters]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/financial/received`, { params: buildParams() });
      setItems(data.items || []);
      setTotal(data.total_received || 0);
    } catch { toast.error("Não foi possível carregar os pagamentos."); }
    finally { setLoading(false); }
  }, [buildParams]);
  useEffect(() => { load(); }, [load]);

  const exportPdf = async () => {
    try {
      const res = await api.get(`/financial/received/pdf`, { params: buildParams(), responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = "pagamentos-recebidos.pdf"; document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch { toast.error("Falha ao exportar PDF."); }
  };

  const setF = (k, v) => setFilters((f) => ({ ...f, [k]: v }));

  return (
    <div className="space-y-4" data-testid="pagamentos-recebidos">
      <div className="bg-[#0F1520] border border-[#263147] rounded-2xl p-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <div className="space-y-1">
          <Label className="text-slate-300 text-xs">De</Label>
          <Input type="date" data-testid="filter-start-input" value={filters.start} onChange={(e) => setF("start", e.target.value)} className={selectCls} />
        </div>
        <div className="space-y-1">
          <Label className="text-slate-300 text-xs">Até</Label>
          <Input type="date" data-testid="filter-end-input" value={filters.end} onChange={(e) => setF("end", e.target.value)} className={selectCls} />
        </div>
        <div className="space-y-1">
          <Label className="text-slate-300 text-xs">Aluno</Label>
          <Select value={filters.student_id} onValueChange={(v) => setF("student_id", v)}>
            <SelectTrigger data-testid="filter-student-select" className={selectCls}><SelectValue /></SelectTrigger>
            <SelectContent className="bg-[#181F2C] border-[#263147] text-slate-100">
              <SelectItem value="all">Todos os alunos</SelectItem>
              {students.map((s) => <SelectItem key={s.id} value={s.id}>{s.full_name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <Label className="text-slate-300 text-xs">Forma</Label>
          <Select value={filters.method} onValueChange={(v) => setF("method", v)}>
            <SelectTrigger data-testid="filter-method-select" className={selectCls}><SelectValue /></SelectTrigger>
            <SelectContent className="bg-[#181F2C] border-[#263147] text-slate-100">
              <SelectItem value="all">Todas as formas</SelectItem>
              {METHODS.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="flex items-center justify-between">
        <p className="text-sm text-slate-400">{items.length} pagamento(s) • recebido {formatBRL(total)}</p>
        <div className="flex items-center gap-2">
          <Button type="button" variant="ghost" size="sm" onClick={() => setFilters({ start: "", end: "", student_id: "all", method: "all" })} className="text-slate-400 hover:text-slate-200"><RotateCcw className="w-4 h-4 mr-1" /> Limpar</Button>
          <Button type="button" data-testid="export-pdf-button" onClick={exportPdf} disabled={items.length === 0} className="h-9 bg-emerald-500 hover:bg-emerald-600 text-[#0A0D12] font-semibold rounded-lg px-3"><FileDown className="w-4 h-4 mr-1" /> Exportar PDF</Button>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20 text-slate-400"><Loader2 className="w-6 h-6 animate-spin mr-2" /> Carregando...</div>
      ) : items.length === 0 ? (
        <div className="text-center py-16 text-slate-500 text-sm">Nenhum pagamento encontrado com esses filtros.</div>
      ) : (
        <div className="space-y-2" data-testid="received-list">
          {items.map((it) => (
            <div key={it.pid} data-testid="received-row" className="bg-[#0F1520] border border-[#263147] rounded-xl px-3 py-2.5 flex items-center gap-3 text-sm">
              <span className="text-slate-300 tabular-nums w-[92px] shrink-0">{formatDateBR(it.date)}</span>
              <span className="text-slate-100 flex-1 min-w-0 truncate">{it.student_name}</span>
              {it.is_bonificado ? (
                <span className="text-blue-400 font-medium">Bonificação</span>
              ) : (
                <span className="text-emerald-400 font-semibold tabular-nums">{formatBRL(it.amount)}</span>
              )}
              <span className={`text-xs px-2 py-0.5 rounded-full border shrink-0 ${it.is_bonificado ? "bg-blue-500/15 text-blue-400 border-blue-500/30" : "bg-[#181F2C] text-slate-400 border-[#263147]"}`}>{it.label}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ------------------------- Parcelas e Créditos ------------------------- */
function ParcelasCreditos() {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get(`/financial/installments`); setPlans(data.plans || []); }
    catch { toast.error("Não foi possível carregar as parcelas."); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-4" data-testid="parcelas-creditos">
      <p className="text-sm text-slate-400">{plans.length} plano(s) de Crédito Parcelado</p>
      {loading ? (
        <div className="flex items-center justify-center py-20 text-slate-400"><Loader2 className="w-6 h-6 animate-spin mr-2" /> Carregando...</div>
      ) : plans.length === 0 ? (
        <div className="text-center py-16 text-slate-500 text-sm">Nenhum aluno com Crédito Parcelado.</div>
      ) : (
        <div className="space-y-3">
          {plans.map((p, idx) => {
            const pct = p.total_parcelas > 0 ? Math.round((p.pagas / p.total_parcelas) * 100) : 0;
            return (
              <div key={`${p.student_id}-${idx}`} data-testid="plan-row" className="bg-[#0F1520] border border-[#263147] rounded-2xl p-4">
                <div className="flex items-center gap-2 mb-3">
                  <Layers className="w-4 h-4 text-emerald-400 shrink-0" />
                  <p className="text-slate-100 font-medium truncate">{p.student_name}{p.nickname && <span className="text-slate-500 text-xs font-normal"> ({p.nickname})</span>}</p>
                  <span className="ml-auto text-xs px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-400 border border-emerald-500/30 shrink-0">Parcela {p.parcela_atual}/{p.total_parcelas}</span>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
                  <div><p className="text-xs text-slate-500">Valor total</p><p className="text-slate-100 font-semibold tabular-nums">{formatBRL(p.valor_total)}</p></div>
                  <div><p className="text-xs text-slate-500">Valor/parcela</p><p className="text-slate-100 font-semibold tabular-nums">{formatBRL(p.valor_parcela)}</p></div>
                  <div><p className="text-xs text-slate-500">Próx. vencimento</p><p className="text-slate-100 tabular-nums">{formatDateBR(p.next_due)}</p></div>
                  <div><p className="text-xs text-slate-500">Pagas / restantes</p><p className="text-slate-100 tabular-nums"><span className="text-emerald-400">{p.pagas}</span> / {p.restantes}</p></div>
                </div>
                <div className="h-2 rounded-full bg-[#181F2C] overflow-hidden mt-3">
                  <div className="h-full bg-emerald-500 rounded-full transition-all duration-500" style={{ width: `${pct}%` }} />
                </div>
                {p.renewal_alert && (
                  <div data-testid="renewal-alert" className="mt-3 flex items-center gap-2 bg-amber-500/10 border border-amber-500/30 rounded-lg px-3 py-2 text-xs text-amber-400">
                    <AlertTriangle className="w-4 h-4 shrink-0" /> Última parcela vence em {formatDateBR(p.last_due)} — hora de renovar o plano.
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

/* ------------------------- Inadimplentes ------------------------- */
const DEFAULT_WA_TEMPLATE = "Olá {nome}! 👋 Passando para lembrar do pagamento em aberto de {valor}, vencido há {dias} dia(s). Assim que possível, me avise para acertarmos. Obrigado! 💪";

function Inadimplentes({ onOpenStudent }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [template, setTemplate] = useState(DEFAULT_WA_TEMPLATE);
  const [tplDialog, setTplDialog] = useState(false);
  const [tplDraft, setTplDraft] = useState("");
  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get(`/financial/defaulters`); setItems(data.defaulters || []); }
    catch { toast.error("Não foi possível carregar os inadimplentes."); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get(`/settings/whatsapp-template`).then(({ data }) => setTemplate(data.template || DEFAULT_WA_TEMPLATE)).catch(() => {}); }, []);

  const registrar = async (it) => {
    if (!it.oldest_pid) { toast.error("Não há parcela para registrar."); return; }
    try {
      await api.patch(`/financial/payment`, { student_id: it.student_id, pid: it.oldest_pid, received: true });
      toast.success("Pagamento registrado!");
      load();
    } catch { toast.error("Falha ao registrar pagamento."); }
  };

  const buildMsg = (it) => (template || DEFAULT_WA_TEMPLATE)
    .replace(/{nome}/g, it.student_name || "")
    .replace(/{valor}/g, formatBRL(it.valor_devido))
    .replace(/{dias}/g, String(it.days_overdue));

  const cobrarWhatsApp = (it) => {
    const digits = (it.phone || "").replace(/\D/g, "");
    if (!digits) { toast.error("Aluno sem telefone cadastrado."); return; }
    const intl = digits.length <= 11 ? `55${digits}` : digits;
    window.open(`https://wa.me/${intl}?text=${encodeURIComponent(buildMsg(it))}`, "_blank");
  };

  const openTplDialog = () => { setTplDraft(template); setTplDialog(true); };
  const saveTemplate = async () => {
    try {
      const { data } = await api.put(`/settings/whatsapp-template`, { template: tplDraft });
      setTemplate(data.template); setTplDialog(false); toast.success("Mensagem salva!");
    } catch { toast.error("Falha ao salvar mensagem."); }
  };

  return (
    <div className="space-y-4" data-testid="inadimplentes">
      <div className="flex items-center justify-between gap-2">
        <p className="text-sm text-slate-400">{items.length} aluno(s) em atraso</p>
        <Button type="button" variant="outline" size="sm" data-testid="edit-wa-template-button" onClick={openTplDialog} className="h-9 border-[#263147] bg-[#181F2C] text-slate-200 hover:bg-[#1E2738] rounded-lg px-3"><Settings className="w-4 h-4 sm:mr-1" /><span className="hidden sm:inline">Editar mensagem</span></Button>
      </div>
      {loading ? (
        <div className="flex items-center justify-center py-20 text-slate-400"><Loader2 className="w-6 h-6 animate-spin mr-2" /> Carregando...</div>
      ) : items.length === 0 ? (
        <div className="text-center py-16 text-slate-500 text-sm">Nenhum aluno inadimplente. 🎉</div>
      ) : (
        <div className="space-y-2">
          {items.map((it) => (
            <div key={it.student_id} data-testid="defaulter-row" className="bg-[#0F1520] border border-red-500/40 rounded-xl p-3 flex flex-col sm:flex-row sm:items-center gap-3">
              <div className="flex items-center gap-2 flex-1 min-w-0">
                <UserX className="w-4 h-4 text-red-400 shrink-0" />
                <div className="min-w-0">
                  <p className="text-slate-100 font-medium truncate">{it.student_name}{it.nickname && <span className="text-slate-500 text-xs font-normal"> ({it.nickname})</span>}</p>
                  <p className="text-xs text-slate-400 flex items-center gap-1"><Phone className="w-3 h-3" /> {it.phone || "—"}</p>
                </div>
              </div>
              <div className="flex items-center gap-4 sm:gap-6">
                <div className="text-right">
                  <span className="text-xs px-2 py-0.5 rounded-full bg-red-500/15 text-red-400 border border-red-500/30">{it.days_overdue} dia(s) em atraso</span>
                  {it.count_overdue > 1 && <p className="text-[11px] text-slate-500 mt-0.5">{it.count_overdue} parcelas vencidas</p>}
                  <p className="text-red-400 font-semibold tabular-nums mt-1">{formatBRL(it.valor_devido)}</p>
                </div>
                <div className="flex flex-col sm:flex-row gap-2">
                  <Button type="button" size="sm" data-testid="defaulter-whatsapp-button" onClick={() => cobrarWhatsApp(it)} className="h-9 bg-[#25D366]/15 text-[#25D366] border border-[#25D366]/30 hover:bg-[#25D366]/25 rounded-lg px-3"><MessageCircle className="w-4 h-4 sm:mr-1" /><span className="hidden sm:inline">Cobrar</span></Button>
                  <Button type="button" size="sm" data-testid="defaulter-register-button" onClick={() => registrar(it)} className="h-9 bg-emerald-500 hover:bg-emerald-600 text-[#0A0D12] font-semibold rounded-lg px-3"><Check className="w-4 h-4 sm:mr-1" /><span className="hidden sm:inline">Registrar</span></Button>
                  <Button type="button" size="sm" variant="outline" data-testid="defaulter-view-button" onClick={() => onOpenStudent?.(it.student_id)} className="h-9 border-[#263147] bg-[#181F2C] text-slate-200 hover:bg-[#1E2738] rounded-lg px-3"><FileText className="w-4 h-4 sm:mr-1" /><span className="hidden sm:inline">Ver Ficha</span></Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={tplDialog} onOpenChange={setTplDialog}>
        <DialogContent className="bg-[#0F1520] border-[#263147] text-slate-100">
          <DialogHeader><DialogTitle>Editar mensagem de cobrança</DialogTitle><DialogDescription className="text-slate-400">Personalize o texto enviado por WhatsApp aos inadimplentes.</DialogDescription></DialogHeader>
          <div className="space-y-2">
            <Textarea data-testid="wa-template-textarea" value={tplDraft} onChange={(e) => setTplDraft(e.target.value)} rows={5} className="bg-[#181F2C] border-[#263147] text-slate-100 rounded-lg" />
            <p className="text-xs text-slate-500">Use os marcadores <span className="text-emerald-400">{"{nome}"}</span>, <span className="text-emerald-400">{"{valor}"}</span> e <span className="text-emerald-400">{"{dias}"}</span> — serão preenchidos automaticamente.</p>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setTplDialog(false)} className="border-[#263147] bg-[#181F2C] text-slate-200 hover:bg-[#1E2738]">Cancelar</Button>
            <Button type="button" data-testid="wa-template-save-button" onClick={saveTemplate} className="bg-emerald-500 hover:bg-emerald-600 text-[#0A0D12] font-semibold">Salvar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ------------------------- Relatórios ------------------------- */
function Relatorios() {
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get(`/financial/report`, { params: { month } }); setData(data); }
    catch { toast.error("Não foi possível carregar o relatório."); }
    finally { setLoading(false); }
  }, [month]);
  useEffect(() => { load(); }, [load]);

  const exportPdf = async () => {
    try {
      const res = await api.get(`/financial/report/pdf`, { params: { month }, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a"); a.href = url; a.download = `relatorio-financeiro-${month}.pdf`;
      document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
    } catch { toast.error("Falha ao exportar PDF."); }
  };

  const methodColors = { "Pix": "bg-emerald-500", "Dinheiro": "bg-green-500", "Crédito à Vista": "bg-teal-500", "Crédito Parcelado": "bg-amber-500" };

  return (
    <div className="space-y-5" data-testid="relatorios">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <label className="text-xs text-slate-400">Mês</label>
          <input type="month" data-testid="report-month-input" value={month} onChange={(e) => setMonth(e.target.value)} className={inputCls} />
        </div>
        <Button type="button" data-testid="report-export-pdf-button" onClick={exportPdf} className="h-10 bg-emerald-500 hover:bg-emerald-600 text-[#0A0D12] font-semibold rounded-lg px-3"><FileDown className="w-4 h-4 mr-1" /> Exportar PDF</Button>
      </div>
      {loading ? (
        <div className="flex items-center justify-center py-20 text-slate-400"><Loader2 className="w-6 h-6 animate-spin mr-2" /> Carregando...</div>
      ) : !data ? null : (
        <>
          <div data-testid="report-comparativo" className="bg-[#0F1520] border border-[#263147] rounded-2xl p-5">
            <p className="text-sm text-slate-300 font-medium mb-4 flex items-center gap-2"><BarChart3 className="w-4 h-4 text-emerald-400" /> Comparativo de recebimentos</p>
            {(() => {
              const max = Math.max(data.received_month, data.received_prev, 1);
              const up = data.diff >= 0;
              return (
                <div className="space-y-3">
                  <div>
                    <div className="flex justify-between text-xs mb-1"><span className="text-slate-400">{monthLabel(data.prev_month)}</span><span className="text-slate-300 tabular-nums">{formatBRL(data.received_prev)}</span></div>
                    <div className="h-3 rounded-full bg-[#181F2C] overflow-hidden"><div className="h-full bg-slate-500 rounded-full transition-all duration-500" style={{ width: `${(data.received_prev / max) * 100}%` }} /></div>
                  </div>
                  <div>
                    <div className="flex justify-between text-xs mb-1"><span className="text-slate-400">{monthLabel(data.month)}</span><span className="text-emerald-400 font-semibold tabular-nums">{formatBRL(data.received_month)}</span></div>
                    <div className="h-3 rounded-full bg-[#181F2C] overflow-hidden"><div className="h-full bg-emerald-500 rounded-full transition-all duration-500" style={{ width: `${(data.received_month / max) * 100}%` }} /></div>
                  </div>
                  <div className={`flex items-center gap-1.5 text-sm font-medium ${up ? "text-emerald-400" : "text-red-400"}`} data-testid="report-diff">
                    {up ? <ArrowUpRight className="w-4 h-4" /> : <ArrowDownRight className="w-4 h-4" />}
                    {formatBRL(Math.abs(data.diff))} ({data.diff_pct}%) {up ? "acima" : "abaixo"} do mês anterior
                  </div>
                </div>
              );
            })()}
          </div>

          <div data-testid="report-by-method" className="bg-[#0F1520] border border-[#263147] rounded-2xl p-5">
            <p className="text-sm text-slate-300 font-medium mb-4">Recebido por forma de pagamento</p>
            {(() => {
              const entries = Object.entries(data.by_method);
              const max = Math.max(...entries.map(([, v]) => v), 1);
              return (
                <div className="space-y-3">
                  {entries.map(([k, v]) => (
                    <div key={k}>
                      <div className="flex justify-between text-xs mb-1"><span className="text-slate-300">{k}</span><span className="text-slate-100 tabular-nums font-medium">{formatBRL(v)}</span></div>
                      <div className="h-2.5 rounded-full bg-[#181F2C] overflow-hidden"><div className={`h-full rounded-full transition-all duration-500 ${methodColors[k] || "bg-emerald-500"}`} style={{ width: `${(v / max) * 100}%` }} /></div>
                    </div>
                  ))}
                  <div className="flex items-center justify-between pt-1 text-sm">
                    <span className="text-blue-400 flex items-center gap-1.5"><span className="w-3 h-3 rounded-full bg-blue-500" /> Bonificação</span>
                    <span className="text-slate-400">{data.bonificado_count} lançamento(s)</span>
                  </div>
                </div>
              );
            })()}
          </div>

          <div data-testid="report-ranking" className="bg-[#0F1520] border border-[#263147] rounded-2xl p-5">
            <p className="text-sm text-slate-300 font-medium mb-3">Alunos ativos vs inadimplentes</p>
            <div className="grid grid-cols-3 gap-3 mb-4 text-center">
              <div><p className="text-2xl font-bold text-slate-100 tabular-nums">{data.active_count}</p><p className="text-xs text-slate-500">Ativos</p></div>
              <div><p className="text-2xl font-bold text-emerald-400 tabular-nums">{data.up_to_date}</p><p className="text-xs text-slate-500">Em dia</p></div>
              <div><p className="text-2xl font-bold text-red-400 tabular-nums">{data.overdue}</p><p className="text-xs text-slate-500">Inadimplentes</p></div>
            </div>
            {data.defaulters_ranking?.length > 0 && (
              <div className="space-y-1.5">
                <p className="text-xs text-slate-500 mb-1">Ranking de inadimplentes (maior dívida)</p>
                {data.defaulters_ranking.map((r, i) => (
                  <div key={`${r.student_name}-${i}`} data-testid="ranking-row" className="flex items-center gap-2 bg-[#181F2C] border border-[#263147] rounded-lg px-3 py-2 text-sm">
                    <span className="text-slate-500 w-6">{i + 1}º</span>
                    <span className="text-slate-200 flex-1 min-w-0 truncate">{r.student_name}</span>
                    <span className="text-xs text-slate-500 shrink-0">{r.days_overdue}d</span>
                    <span className="text-red-400 font-semibold tabular-nums shrink-0">{formatBRL(r.valor_devido)}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
          <RelatorioAnual />
        </>
      )}
    </div>
  );
}

function RelatorioAnual() {
  const [year, setYear] = useState(new Date().getFullYear());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get(`/financial/report/annual`, { params: { year } }); setData(data); }
    catch { toast.error("Não foi possível carregar o relatório anual."); }
    finally { setLoading(false); }
  }, [year]);
  useEffect(() => { load(); }, [load]);
  const short = ["Jan", "Fev", "Mar", "Abr", "Mai", "Jun", "Jul", "Ago", "Set", "Out", "Nov", "Dez"];
  const max = data ? Math.max(...data.months.map((m) => m.received), 1) : 1;
  return (
    <div data-testid="report-annual" className="bg-[#0F1520] border border-[#263147] rounded-2xl p-5">
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-slate-300 font-medium flex items-center gap-2"><BarChart3 className="w-4 h-4 text-emerald-400" /> Receita mês a mês</p>
        <div className="flex items-center gap-2">
          <button type="button" data-testid="annual-prev-year" onClick={() => setYear((y) => y - 1)} className="w-7 h-7 rounded-lg bg-[#181F2C] border border-[#263147] text-slate-300 hover:text-slate-100">‹</button>
          <span data-testid="annual-year" className="text-slate-100 font-semibold tabular-nums w-12 text-center">{year}</span>
          <button type="button" data-testid="annual-next-year" onClick={() => setYear((y) => Math.min(y + 1, new Date().getFullYear()))} className="w-7 h-7 rounded-lg bg-[#181F2C] border border-[#263147] text-slate-300 hover:text-slate-100">›</button>
        </div>
      </div>
      {loading ? (
        <div className="flex items-center justify-center py-16 text-slate-400"><Loader2 className="w-6 h-6 animate-spin mr-2" /> Carregando...</div>
      ) : !data ? null : (
        <>
          <div className="flex items-end gap-1 h-44">
            {data.months.map((m) => (
              <div key={m.month} data-testid="annual-bar" className="flex-1 flex flex-col items-center justify-end h-full" title={`${short[m.month - 1]}: ${formatBRL(m.received)}`}>
                <div className={`w-full rounded-t-md min-h-[2px] ${m.received > 0 ? "bg-emerald-500" : "bg-[#181F2C]"}`} style={{ height: `${(m.received / max) * 100}%` }} />
                <span className="text-[9px] text-slate-500 mt-1">{short[m.month - 1]}</span>
              </div>
            ))}
          </div>
          <p className="text-sm text-slate-400 mt-3">Total em {year}: <span className="text-emerald-400 font-semibold">{formatBRL(data.total)}</span></p>
        </>
      )}
    </div>
  );
}

/* ------------------------- Página ------------------------- */
export default function Financeiro({ onOpenStudent, initialSection }) {
  const [section, setSection] = useState(initialSection || "resumo");
  const tabs = [
    { key: "resumo", label: "Resumo do Mês", testid: "fin-tab-resumo" },
    { key: "receber", label: "Contas a Receber", testid: "fin-tab-receber" },
    { key: "recebidos", label: "Pagamentos Recebidos", testid: "fin-tab-recebidos" },
    { key: "parcelas", label: "Parcelas e Créditos", testid: "fin-tab-parcelas" },
    { key: "inadimplentes", label: "Inadimplentes", testid: "fin-tab-inadimplentes" },
    { key: "relatorios", label: "Relatórios", testid: "fin-tab-relatorios" },
  ];
  return (
    <div data-testid="financeiro-page" className="space-y-6">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold font-heading text-slate-100 mb-1 flex items-center gap-2">
          <Wallet className="w-6 h-6 text-emerald-400" /> Financeiro
        </h1>
        <p className="text-sm text-slate-400">Controle financeiro dos seus alunos.</p>
      </div>
      <div className="flex items-center gap-2 bg-[#181F2C] border border-[#263147] rounded-2xl p-1.5 overflow-x-auto no-scrollbar sm:inline-flex">
        {tabs.map((t) => (
          <button key={t.key} data-testid={t.testid} onClick={() => setSection(t.key)}
            className={`shrink-0 whitespace-nowrap h-10 px-4 rounded-xl text-sm font-medium transition-colors ${section === t.key ? "bg-emerald-500 text-[#0A0D12]" : "text-slate-400 hover:text-slate-200"}`}>
            {t.label}
          </button>
        ))}
      </div>
      <div key={section}>
        {section === "resumo" ? <ResumoMes />
          : section === "receber" ? <ContasReceber />
          : section === "recebidos" ? <PagamentosRecebidos />
          : section === "parcelas" ? <ParcelasCreditos />
          : section === "inadimplentes" ? <Inadimplentes onOpenStudent={onOpenStudent} />
          : <Relatorios />}
      </div>
    </div>
  );
}
